import React, { useState } from "react";
import { getApiBaseUrl } from "../lib/api"; // Import the utility function
import { useAppendTransactions, useUploadKeywords } from "../hooks/useApi";
import { detectCSVFormat, CSV_FORMATS } from "../utils/csvFormatDetector";
import {
  parseChaseFormat,
  parseAppleCardFormat,
  parseGenericFormat,
  parseCSVLine,
} from "../utils/csvParsers";
import PlaidLink from "./PlaidLink";
import InstitutionManager from "./InstitutionManager";
import AmazonOrderMatcher from "./AmazonOrderMatcher";

const TransactionUploader = ({ onUploadSuccess }) => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fastMode, setFastMode] = useState(false);
  const [superFastMode, setSuperFastMode] = useState(false);

  // React Query mutations - only safe append mode supported
  const appendTransactionsMutation = useAppendTransactions();
  const uploadKeywordsMutation = useUploadKeywords();

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadStatus(null);
    }
  };

  const processCSV = async (csvText) => {
    try {
      const lines = csvText.split("\n").filter((line) => line.trim());

      if (lines.length <= 1) {
        throw new Error("CSV file appears to be empty or malformed");
      }

      // Detect CSV format
      const headers = parseCSVLine(lines[0]);
      const detectedFormat = detectCSVFormat(headers);

      console.log(`📄 Detected CSV format: ${detectedFormat}`);

      // Parse using appropriate format-specific parser
      let transactions;
      switch (detectedFormat) {
        case CSV_FORMATS.CHASE:
          console.log("Using Chase CSV parser");
          transactions = parseChaseFormat(lines);
          break;
        case CSV_FORMATS.APPLE_CARD:
          console.log("Using Apple Card CSV parser");
          transactions = parseAppleCardFormat(lines);
          break;
        case CSV_FORMATS.UNKNOWN:
        default:
          console.warn("⚠️  Unknown CSV format, attempting generic parsing");
          transactions = parseGenericFormat(lines);
          break;
      }

      console.log(
        `✅ Parsed ${transactions.length} transactions from ${detectedFormat} format`
      );

      // Return transactions without client-side categorization
      // Server will handle all categorization logic
      return transactions;
    } catch (error) {
      console.error("❌ Error processing CSV:", error);
      throw error;
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      setUploadStatus({
        success: false,
        message: "Please select a file first",
      });
      return;
    }

    if (!selectedFile.name.match(/\.csv$/i)) {
      setUploadStatus({ success: false, message: "Please select a CSV file" });
      return;
    }

    setUploadStatus(null);

    try {
      // Read the file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error("Error reading file"));
        reader.readAsText(selectedFile);
      });

      // Process the CSV content
      const transactions = await processCSV(fileContent);

      // Always use safe append mode (replace mode removed for safety)
      const result = await appendTransactionsMutation.mutateAsync({
        transactions,
        options: {
          skipCategorization: fastMode,
          superFastMode: superFastMode,
        },
      });

      setUploadStatus({
        success: true,
        message:
          result.message ||
          `${
            result.newUnique || result.count
          } transactions processed successfully`,
        count: result.newUnique || result.count,
        total: result.total,
        duplicates: result.duplicates,
        performance: result.performance, // Include performance data
      });

      // Notify parent component of successful upload
      if (onUploadSuccess) {
        onUploadSuccess(transactions);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadStatus({
        success: false,
        message: `Upload failed: ${error.message}`,
      });
    }
  };

  const uploadKeywordsFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      await uploadKeywordsMutation.mutateAsync(formData);

      setUploadStatus({
        success: true,
        message: "Keywords file uploaded successfully!",
      });
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadStatus({
        success: false,
        message: `Upload failed: ${error.message}`,
      });
    }
  };

  const handleKeywordsFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      uploadKeywordsFile(e.target.files[0]);
    }
  };

  const [refreshInstitutions, setRefreshInstitutions] = useState(0);

  const handlePlaidSuccess = (institution) => {
    console.log("Institution connected:", institution);
    // Refresh institutions list by incrementing the key
    setRefreshInstitutions(prev => prev + 1);
    // Show success message
    setUploadStatus({
      success: true,
      message: `Successfully connected ${institution.institution_name}. You can now sync transactions.`,
    });
  };

  const handleSyncComplete = (data) => {
    setUploadStatus({
      success: true,
      message: data.message || `Successfully synced ${data.count} transactions`,
      count: data.count,
      performance: data.performance,
    });

    // Notify parent component to refresh transaction data
    if (onUploadSuccess) {
      onUploadSuccess([]); // Plaid sync doesn't return transaction array, but triggers refresh
    }
  };

  const handlePlaidExit = (err, metadata) => {
    if (err) {
      console.error('Plaid Link exit error:', err);
      setUploadStatus({
        success: false,
        message: err.error_message || 'Connection cancelled',
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        Upload Transactions
      </h2>

      {/* Plaid Section */}
      <div className="mb-6 pb-6 border-b border-gray-200">
        <h3 className="text-md font-semibold text-gray-700 mb-3">
          Connect Bank Account (Plaid)
        </h3>
        <div className="mb-4">
          <PlaidLink onSuccess={handlePlaidSuccess} onExit={handlePlaidExit} />
        </div>
        <div className="mt-4">
          <InstitutionManager 
            refreshKey={refreshInstitutions}
            onSyncComplete={(data) => {
              handleSyncComplete(data);
              // Refresh institutions list after sync
              setRefreshInstitutions(prev => prev + 1);
            }} 
          />
        </div>
      </div>

      <AmazonOrderMatcher />

      {/* CSV Upload Section - Hidden for now */}
      {false && (
        <>
          <div>
            <h3 className="text-md font-semibold text-gray-700 mb-3">
              Upload CSV File
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-purple-50 file:text-purple-700
                hover:file:bg-purple-100"
            />
            <p className="mt-1 text-xs text-gray-500">
              Supports Chase and Apple Card CSV formats. Other formats will be
              auto-detected. Transactions are auto-categorized using keywords.
            </p>
          </div>

          {/* Safe Upload Info */}
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-start">
              <div className="text-green-600 mr-2">✅</div>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Smart Upload Mode
                </p>
                <p className="text-xs text-green-700">
                  Safely adds new transactions, skips duplicates, and preserves your
                  manual category edits. You can upload the same CSV multiple times
                  without creating duplicates.
                </p>
              </div>
            </div>
          </div>

          {/* Fast Mode Option */}
          <div className="mb-4 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={fastMode}
                onChange={(e) => {
                  setFastMode(e.target.checked);
                  if (e.target.checked) setSuperFastMode(false); // Disable super fast if fast is enabled
                }}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Fast Mode ⚡
                <span className="text-gray-500 block text-xs">
                  Skip auto-categorization for faster uploads. You can categorize
                  transactions manually later.
                </span>
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={superFastMode}
                onChange={(e) => {
                  setSuperFastMode(e.target.checked);
                  if (e.target.checked) setFastMode(false); // Disable fast if super fast is enabled
                }}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Super Fast Mode 🚀
                <span className="text-gray-500 block text-xs">
                  Skip categorization AND duplicate checking for maximum speed. May
                  create duplicates!
                </span>
              </span>
            </label>
          </div>

          <button
            onClick={uploadFile}
            disabled={appendTransactionsMutation.isPending || !selectedFile}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              appendTransactionsMutation.isPending || !selectedFile
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {appendTransactionsMutation.isPending ? (
              <>
                <span className="animate-spin inline-block mr-2">⏳</span>
                Uploading...
              </>
            ) : (
              `Upload Transactions${
                superFastMode
                  ? " 🚀 (Super Fast)"
                  : fastMode
                  ? " ⚡ (Fast Mode)"
                  : ""
              }`
            )}
          </button>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Keywords JSON (optional)
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleKeywordsFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-green-50 file:text-green-700
                hover:file:bg-green-100"
            />
              <p className="mt-1 text-xs text-gray-500">
                Upload a keywords JSON to update categories based on keywords
              </p>
            </div>

            {uploadKeywordsMutation.isPending && (
              <div className="mt-4 text-sm text-gray-500">
                <p>Uploading keywords file...</p>
              </div>
            )}
          </div>
        </>
      )}

      {uploadStatus && (
        <div
          className={`mt-4 p-3 rounded-md ${
            uploadStatus.success
              ? "bg-green-50 text-green-800"
              : "bg-red-50 text-red-800"
          }`}
        >
          <p>{uploadStatus.message}</p>
          {uploadStatus.success && uploadStatus.count !== undefined && (
            <div className="mt-2 text-sm">
              {uploadStatus.total !== undefined ? (
                <div>
                  <p>✅ {uploadStatus.count} transactions processed</p>
                  {uploadStatus.duplicates > 0 && (
                    <p>⏭️ {uploadStatus.duplicates} duplicates skipped</p>
                  )}
                  <p>📊 Total transactions: {uploadStatus.total}</p>
                </div>
              ) : (
                <p>📁 {uploadStatus.count} transactions processed</p>
              )}
              {uploadStatus.performance && (
                <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                  <p>
                    ⚡ Performance:{" "}
                    {uploadStatus.performance.total_time_seconds.toFixed(2)}s
                    total
                  </p>
                  {uploadStatus.performance.categorization_time_seconds > 0 && (
                    <p>
                      🤖 Categorization:{" "}
                      {uploadStatus.performance.categorization_time_seconds.toFixed(
                        2
                      )}
                      s
                    </p>
                  )}
                  <p>
                    💾 Database:{" "}
                    {uploadStatus.performance.upsert_time_seconds.toFixed(2)}s
                  </p>
                  {uploadStatus.performance.skipped_categorization && (
                    <p>⚡ Fast mode enabled - categorization skipped</p>
                  )}
                  {uploadStatus.performance.super_fast_mode && (
                    <p>
                      🚀 Super fast mode enabled - categorization and duplicate
                      checking skipped
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionUploader;
